// API client for backend calls
import { getFirebaseAuth } from '../firebase';
import type { GeoJSONFeature } from '../types';

const API_BASE = '/api';

async function getAuthHeaders(): Promise<HeadersInit> {
  const auth = await getFirebaseAuth();
  const user = auth.currentUser;
  const token = user ? await user.getIdToken() : '';
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
}

export interface POI {
  place_name: string | null;
  lat: number;
  lon: number;
  type: string;
  photo_url: string | null;
  source: string;
}

// Fetch POIs for a region
export async function fetchPOIs(params: {
  l2_id?: string;
  bbox?: { minLng: number; minLat: number; maxLng: number; maxLat: number };
  types?: string[];
}): Promise<POI[]> {
  const searchParams = new URLSearchParams();
  if (params.l2_id) searchParams.set('l2_id', params.l2_id);
  if (params.bbox) {
    searchParams.set('bbox', `${params.bbox.minLng},${params.bbox.minLat},${params.bbox.maxLng},${params.bbox.maxLat}`);
  }
  if (params.types) {
    searchParams.set('types', params.types.join(','));
  }
  
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/poi?${searchParams}`, { headers });
  
  if (!response.ok) {
    throw new Error(`POI fetch failed: ${response.statusText}`);
  }
  return response.json();
}

// Risk Assessment Types
export interface RiskSummary {
  unit_id: string;
  unit_type: string;
  country: string;
  area_name: string;
  extremism_susceptibility: number | null;
  foreign_influence_exposure: number | null;
  humanitarian_vulnerability: number | null;
  information_void: number | null;
  economic_grievance: number | null;
  governance_distrust: number | null;
  social_cohesion_fragility: number | null;
  security_instability: number | null;
  summary: string;
  survey_sample_size: number;
  quarter: string; // Server returns FORMAT_DATE('%Y-%m-%d', quarter)
  created_at: string;
}

export interface RiskQuartersResponse {
  available: string[];
  existing: string[];
}

export interface RiskSummariesResponse {
  summaries: RiskSummary[];
}

export interface RiskGenerateResponse {
  success: boolean;
  summary: RiskSummary;
}

// Fetch all risk summaries for a unit
export async function fetchRiskSummaries(unitId: string): Promise<RiskSummariesResponse> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/risk/summaries/${encodeURIComponent(unitId)}`, { headers });
  
  if (!response.ok) {
    throw new Error(`Risk summaries fetch failed: ${response.statusText}`);
  }
  return response.json();
}

// Fetch available quarters for generation
export async function fetchAvailableQuarters(unitId: string): Promise<RiskQuartersResponse> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/risk/quarters/${encodeURIComponent(unitId)}`, { headers });
  
  if (!response.ok) {
    throw new Error(`Quarters fetch failed: ${response.statusText}`);
  }
  return response.json();
}

// Generate risk assessment for a specific quarter
export async function generateRiskAssessment(unitId: string, quarter: string): Promise<RiskGenerateResponse> {
  const headers = await getAuthHeaders();
  
  const response = await fetch(`${API_BASE}/risk/generate`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ unit_id: unitId, quarter }),
  });
  if (!response.ok) {
    throw new Error(`Risk generation failed: ${response.statusText}`);
  }
  return response.json();
}

// POI Types
export interface POIFeature {
  type: 'Feature';
  properties: {
    feature_id: string;
    source: 'premise' | 'osm';
    category: string;
    feature_type: string;
    place_name: string | null;
    photo_url: string | null;
    num_submissions: number | null;
    last_validated: string | null;
  };
  geometry: {
    type: 'Point';
    coordinates: [number, number];
  };
}

export interface POIResponse {
  type: 'FeatureCollection';
  features: POIFeature[];
  meta: {
    total: number;
    bbox: [number, number, number, number];
    categories: string[];
  };
}

// Fetch POIs by bounding box and categories
export async function fetchPOIsByBbox(params: {
  bbox: [number, number, number, number]; // [minLng, minLat, maxLng, maxLat]
  categories: string[];
  limit?: number;
}): Promise<POIResponse> {
  const searchParams = new URLSearchParams({
    bbox: params.bbox.join(','),
    categories: params.categories.join(','),
  });
  if (params.limit) {
    searchParams.set('limit', params.limit.toString());
  }
  
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/poi?${searchParams}`, { headers });
  
  if (!response.ok) {
    throw new Error(`POI fetch failed: ${response.statusText}`);
  }
  return response.json();
}

// Fetch secure URL for PMTiles
export async function fetchTileUrl(): Promise<string> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/tiles/url`, { headers });
  
  if (!response.ok) {
    throw new Error(`Tile URL fetch failed: ${response.statusText}`);
  }
  const data = await response.json();
  return data.url;
}

export interface InfrastructureFeature {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number];  // [lon, lat]
  };
  properties: {
    id: string;
    infraType: 'cell' | 'wifi';
    // Cell properties
    network_type?: string;
    provider?: string;
    mcc?: string;
    cell_ids?: string[];
    power?: number;  // max_observed_power
    // WiFi properties
    ssid?: string;
    bssid?: string;
    oui?: string;
    manufacturer?: string;
    manufacturer_country?: string;
    capabilities?: string[];
    wifi_type?: 'stationary' | 'mobile' | 'unknown';
    // Common
    signal: number;
    observations: number;
    last_seen: string | null;
  };
}

export interface InfrastructureResponse {
  type: 'FeatureCollection';
  features: InfrastructureFeature[];
  meta: {
    total: number;
    bbox: [number, number, number, number];
  };
}

export interface SearchSuggestion {
  value: string;
  count: number;
  extra?: Record<string, unknown>;
}

// Fetch cell towers by bounding box
export async function fetchCellTowers(params: {
  bbox: [number, number, number, number]; // [minLon, minLat, maxLon, maxLat]
  networkTypes?: string[];
  provider?: string;
  limit?: number;
}): Promise<InfrastructureResponse> {
  const searchParams = new URLSearchParams({
    bbox: params.bbox.join(','),
  });
  if (params.networkTypes && params.networkTypes.length > 0) {
    searchParams.set('network_type', params.networkTypes.join(','));
  }
  if (params.provider) {
    searchParams.set('provider', params.provider);
  }
  if (params.limit) {
    searchParams.set('limit', params.limit.toString());
  }
  
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/infrastructure/cell?${searchParams}`, { headers });
  
  if (!response.ok) {
    throw new Error(`Cell tower fetch failed: ${response.statusText}`);
  }
  return response.json();
}

// Fetch WiFi networks by bounding box
export async function fetchWifiNetworks(params: {
  bbox: [number, number, number, number]; // [minLon, minLat, maxLon, maxLat]
  ssid?: string;
  manufacturer?: string;
  types?: string[];
  limit?: number;
}): Promise<InfrastructureResponse> {
  const searchParams = new URLSearchParams({
    bbox: params.bbox.join(','),
  });
  if (params.ssid) {
    searchParams.set('ssid', params.ssid);
  }
  if (params.manufacturer) {
    searchParams.set('manufacturer', params.manufacturer);
  }
  if (params.types && params.types.length > 0) {
    searchParams.set('type', params.types.join(','));
  }
  if (params.limit) {
    searchParams.set('limit', params.limit.toString());
  }
  
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/infrastructure/wifi?${searchParams}`, { headers });
  
  if (!response.ok) {
    throw new Error(`WiFi network fetch failed: ${response.statusText}`);
  }
  return response.json();
}

// Search SSIDs for autocomplete
export async function searchSSIDs(query: string, limit: number = 20): Promise<SearchSuggestion[]> {
  if (query.length < 2) return [];
  
  const searchParams = new URLSearchParams({
    q: query,
    limit: limit.toString(),
  });
  
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/infrastructure/wifi/ssids?${searchParams}`, { headers });
  
  if (!response.ok) {
    throw new Error(`SSID search failed: ${response.statusText}`);
  }
  
  const data = await response.json();
  return data.map((r: { ssid: string; count: number }) => ({
    value: r.ssid,
    count: r.count,
  }));
}

// Search manufacturers for autocomplete
export async function searchManufacturers(query: string, limit: number = 20): Promise<SearchSuggestion[]> {
  if (query.length < 2) return [];
  
  const searchParams = new URLSearchParams({
    q: query,
    limit: limit.toString(),
  });
  
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/infrastructure/wifi/manufacturers?${searchParams}`, { headers });
  
  if (!response.ok) {
    throw new Error(`Manufacturer search failed: ${response.statusText}`);
  }
  
  const data = await response.json();
  return data.map((r: { manufacturer: string; manufacturer_country: string; count: number }) => ({
    value: r.manufacturer,
    count: r.count,
    extra: { country: r.manufacturer_country },
  }));
}

// Search cell providers for autocomplete
export async function searchCellProviders(query: string, limit: number = 20): Promise<SearchSuggestion[]> {
  if (query.length < 2) return [];
  
  const searchParams = new URLSearchParams({
    q: query,
    limit: limit.toString(),
  });
  
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/infrastructure/cell/providers?${searchParams}`, { headers });
  
  if (!response.ok) {
    throw new Error(`Provider search failed: ${response.statusText}`);
  }
  
  const data = await response.json();
  return data.map((r: { network_name: string; mcc: string; count: number; network_types: string[] }) => ({
    value: r.network_name,
    count: r.count,
    extra: { mcc: r.mcc, types: r.network_types },
  }));
}

// Fetch a single infrastructure feature by ID
export async function fetchInfrastructureFeature(
  type: 'cell' | 'wifi',
  featureId: string
): Promise<InfrastructureFeature['properties'] & { coordinates: [number, number] }> {
  const headers = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE}/infrastructure/feature/${type}/${encodeURIComponent(featureId)}`,
    { headers }
  );
  
  if (!response.ok) {
    throw new Error(`Infrastructure feature fetch failed: ${response.statusText}`);
  }
  return response.json();
}

export interface PolygonPhoto {
  id: string;           // submission_id
  lat: number;
  lon: number;
  url: string;          // img_url
  date: string | null;  // photo_date
  organization_id: string;
}

export interface PolygonPhotosResponse {
  photos: PolygonPhoto[];
  total: number;
}

// Fetch photos within a polygon geometry
export async function fetchPolygonPhotos(geometry: unknown): Promise<PolygonPhotosResponse> {
  const headers = await getAuthHeaders();
  
  const response = await fetch(`${API_BASE}/photos`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ 
      geometry,
      limit: 50 
    }),
  });
  
  if (!response.ok) {
    throw new Error(`Photos fetch failed: ${response.statusText}`);
  }
  return response.json();
}

/** One recording within a submission. Video and audio both arrive this way;
 *  `question_name` is what ties a recording to its transcription. */
export interface MediaItem {
  question_name: string | null;
  question_label: string | null;
  question_order: number | null;
  url: string;
}

export interface MyLocationFeature {
  type: 'Feature';
  properties: {
    id: string;           // unique per feature
    submission_id?: string; // original submission_id for API calls
    type: 'photo' | 'video' | 'audio';
    /** First video's URL. Retained so single-media call sites keep working;
     *  `videos` is the complete list. */
    url: string;
    /** A quarter of video submissions carry more than one recording, and a
     *  submission has ONE location — so they arrive together on one feature
     *  rather than as markers metres apart. */
    videos?: MediaItem[];
    video_count?: number;
    /** Audio's equivalent. Over a third of audio submissions carry more than
     *  one — 1,048 of them carry seven — so this matters more than for video. */
    recordings?: MediaItem[];
    recording_count?: number;
    date: string | null;
    organization_id: string;
    /** 'premise-data' for the Premise Data layer. Set explicitly so the map
     *  never has to infer a feature's source from its organisation id. */
    source?: string;
    form_title: string | null;
    campaign_name?: string | null;
  };
  geometry: {
    type: 'Point';
    coordinates: [number, number];  // [lon, lat]
  };
}

export interface MyLocationsResponse {
  type: 'FeatureCollection';
  features: MyLocationFeature[];
  meta: {
    total: number;
  };
}

export interface SubmissionQuestion {
  order: number;
  question: string;
  answer: string;
}

export interface SubmissionDetailsResponse {
  submission_id: string;
  questions: SubmissionQuestion[];
  /** First transcription, for single-recording call sites. */
  video_transcription: string | null;
  /** One per recording, keyed by question_name — a submission with several
   *  videos has a transcription for each, and they are not interchangeable. */
  transcriptions?: {
    question_name: string | null;
    question_order: number | null;
    text: string;
  }[];
}

// Fetch distinct form names for filtering
export async function fetchFormNames(source: 'premise' | 'my-data'): Promise<{ formNames: { name: string; count: number }[] }> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/my-locations/form-names?source=${source}`, { headers });

  if (!response.ok) {
    throw new Error(`Form names fetch failed: ${response.statusText}`);
  }
  return response.json();
}

// Fetch photos from Premise projects (available to all users)
export async function fetchPremisePhotos(params?: {
  bbox?: [number, number, number, number];
  limit?: number;
  formTitles?: string[];
  from?: string; // YYYY-MM-DD, inclusive. Both from + to required together.
  to?: string;
  /** Detected object names to filter on (e.g. ["building","vehicle"]).
   *  Multiple values are OR-ed within the extraction join. */
  objects?: string[];
  /** Wildcard substring against extraction descriptions. */
  description?: string;
}): Promise<MyLocationsResponse> {
  const searchParams = new URLSearchParams();

  if (params?.bbox) {
    searchParams.set('bbox', params.bbox.join(','));
  }
  if (params?.limit) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params?.formTitles && params.formTitles.length > 0) {
    searchParams.set('form_titles', JSON.stringify(params.formTitles));
  }
  if (params?.from && params?.to) {
    searchParams.set('from', params.from);
    searchParams.set('to', params.to);
  }
  if (params?.objects && params.objects.length > 0) {
    searchParams.set('object', JSON.stringify(params.objects));
  }
  if (params?.description && params.description.trim()) {
    searchParams.set('description', params.description.trim());
  }

  const headers = await getAuthHeaders();
  const queryString = searchParams.toString();
  const url = `${API_BASE}/my-locations/premise-photos${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url, { headers });

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = await response.json() as { detail?: string; error?: string };
      detail = body.detail || body.error || detail;
    } catch { /* body wasn't JSON */ }
    throw new Error(`Premise photos fetch failed (${response.status}): ${detail}`);
  }
  return response.json();
}

// Fetch photos for user's organizations
export async function fetchMyPhotos(params?: {
  bbox?: [number, number, number, number];  // [minLng, minLat, maxLng, maxLat]
  limit?: number;
  formTitles?: string[];
  /** English country names; matched case-insensitively server-side. */
  countries?: string[];
  from?: string; // YYYY-MM-DD, inclusive. Both from + to required together.
  to?: string;
  /** Detected object names to filter on. See fetchPremisePhotos. */
  objects?: string[];
  /** Wildcard substring against extraction descriptions. */
  description?: string;
}): Promise<MyLocationsResponse> {
  const searchParams = new URLSearchParams();

  if (params?.bbox) {
    searchParams.set('bbox', params.bbox.join(','));
  }
  if (params?.limit) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params?.formTitles && params.formTitles.length > 0) {
    searchParams.set('form_titles', JSON.stringify(params.formTitles));
  }
  if (params?.countries && params.countries.length > 0) {
    searchParams.set('countries', JSON.stringify(params.countries));
  }
  if (params?.from && params?.to) {
    searchParams.set('from', params.from);
    searchParams.set('to', params.to);
  }
  if (params?.objects && params.objects.length > 0) {
    searchParams.set('object', JSON.stringify(params.objects));
  }
  if (params?.description && params.description.trim()) {
    searchParams.set('description', params.description.trim());
  }

  const headers = await getAuthHeaders();
  const queryString = searchParams.toString();
  const url = `${API_BASE}/my-locations/photos${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url, { headers });

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = await response.json() as { detail?: string; error?: string };
      detail = body.detail || body.error || detail;
    } catch { /* body wasn't JSON */ }
    throw new Error(`My photos fetch failed (${response.status}): ${detail}`);
  }
  return response.json();
}

/**
 * Typeahead for detected object names from `image_data_extraction`.
 * Returns [] when q is shorter than the server minimum (2 chars).
 * The list is capped at 30 and sorted by descending detection count.
 */
export async function fetchObjectNames(q: string): Promise<{
  objects: { name: string; count: number }[];
}> {
  const trimmed = q.trim();
  if (trimmed.length < 2) return { objects: [] };
  const headers = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE}/my-locations/object-names?q=${encodeURIComponent(trimmed)}`,
    { headers },
  );
  if (!response.ok) {
    throw new Error(`Object names fetch failed: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Sentinel-1 SAR thumbnails for an H3 cell.
 * Each view is null when no acquisition was available in its time window
 * (SAR revisits every 6-12 days per orbit; some spots may be gapless).
 * `pngBase64` is a data:image/png;base64 URL ready for <img src>.
 */
export interface SarView {
  pngBase64: string;
  capturedAt: string | null;
}
export interface SarResponse {
  h3: string;
  recent: SarView | null;
  historical: SarView | null;
  change: SarView | null;
  /** Sentinel-2 L2A true-color image, most-recent cloud-free (<20 % CC)
   *  frame within the last 60 days. Null when no clear frame exists. */
  optical: SarView | null;
}
export async function fetchSarForH3(h3Index: string): Promise<SarResponse> {
  const headers = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE}/sar/h3/${encodeURIComponent(h3Index)}`,
    { headers },
  );
  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = (await response.json()) as { detail?: string; error?: string };
      detail = body.detail || body.error || detail;
    } catch { /* body wasn't JSON */ }
    throw new Error(`SAR fetch failed (${response.status}): ${detail}`);
  }
  return response.json();
}

// Fetch videos for user's organizations
export async function fetchMyVideos(params?: {
  bbox?: [number, number, number, number];  // [minLng, minLat, maxLng, maxLat]
  limit?: number;
  formTitles?: string[];
  /** English country names; matched case-insensitively server-side. */
  countries?: string[];
  from?: string; // YYYY-MM-DD, inclusive. Both from + to required together.
  to?: string;
}): Promise<MyLocationsResponse> {
  const searchParams = new URLSearchParams();

  if (params?.bbox) {
    searchParams.set('bbox', params.bbox.join(','));
  }
  if (params?.limit) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params?.formTitles && params.formTitles.length > 0) {
    searchParams.set('form_titles', JSON.stringify(params.formTitles));
  }
  if (params?.countries && params.countries.length > 0) {
    searchParams.set('countries', JSON.stringify(params.countries));
  }
  if (params?.from && params?.to) {
    searchParams.set('from', params.from);
    searchParams.set('to', params.to);
  }

  const headers = await getAuthHeaders();
  const queryString = searchParams.toString();
  const url = `${API_BASE}/my-locations/videos${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url, { headers });

  if (!response.ok) {
    throw new Error(`My videos fetch failed: ${response.statusText}`);
  }
  return response.json();
}

// Fetch audios for user's organizations
export async function fetchMyAudios(params?: {
  bbox?: [number, number, number, number];  // [minLng, minLat, maxLng, maxLat]
  limit?: number;
  formTitles?: string[];
  /** English country names; matched case-insensitively server-side. */
  countries?: string[];
  from?: string; // YYYY-MM-DD, inclusive. Both from + to required together.
  to?: string;
}): Promise<MyLocationsResponse> {
  const searchParams = new URLSearchParams();

  if (params?.bbox) {
    searchParams.set('bbox', params.bbox.join(','));
  }
  if (params?.limit) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params?.formTitles && params.formTitles.length > 0) {
    searchParams.set('form_titles', JSON.stringify(params.formTitles));
  }
  if (params?.countries && params.countries.length > 0) {
    searchParams.set('countries', JSON.stringify(params.countries));
  }
  if (params?.from && params?.to) {
    searchParams.set('from', params.from);
    searchParams.set('to', params.to);
  }

  const headers = await getAuthHeaders();
  const queryString = searchParams.toString();
  const url = `${API_BASE}/my-locations/audios${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url, { headers });

  if (!response.ok) {
    throw new Error(`My audios fetch failed: ${response.statusText}`);
  }
  return response.json();
}

// Fetch Q&A details for a specific submission
export async function fetchSubmissionDetails(submissionId: string): Promise<SubmissionDetailsResponse> {
  const headers = await getAuthHeaders();
  
  const response = await fetch(
    `${API_BASE}/my-locations/submission/${encodeURIComponent(submissionId)}`,
    { headers }
  );
  
  if (!response.ok) {
    throw new Error(`Submission details fetch failed: ${response.statusText}`);
  }
  return response.json();
}

// Fetch a single my-location feature by submission_id (for alert navigation)
export async function fetchMyLocationFeature(submissionId: string): Promise<MyLocationFeature> {
  const headers = await getAuthHeaders();

  const response = await fetch(
    `${API_BASE}/my-locations/feature/${encodeURIComponent(submissionId)}`,
    { headers }
  );

  if (!response.ok) {
    throw new Error(`My location feature fetch failed: ${response.statusText}`);
  }
  return response.json();
}

// Fetch signed URL for video playback from GCS
export async function fetchVideoSignedUrl(gcsUrl: string): Promise<string> {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({ url: gcsUrl });

  const response = await fetch(
    `${API_BASE}/my-locations/video-url?${params}`,
    { headers }
  );

  if (!response.ok) {
    throw new Error(`Video signed URL fetch failed: ${response.statusText}`);
  }
  const data = await response.json();
  return data.signedUrl;
}

export interface NewsEvent {
  article_url: string;
  article_date: string;
  location_number: number;
  location_name: string;
  location_type: string;
  location_event: string;
  place_id: string;
  formatted_address: string;
  l0_country: string;
  l1_province: string;
  l2_district: string;
  l3_locality: string;
  article_country: string;
  article_confidence: number;
  // Article details from search_data
  title?: string;
  translated_title?: string;
  snippet?: string;
  short_summary?: string;
  summary?: string;
  content?: string;
  content_translated?: string;
  diplomatic_score?: number;
  informational_score?: number;
  military_score?: number;
  economic_score?: number;
  policy_regulatory_score?: number;
  thumbnail?: string;
  source_language?: string;
}

export interface NewsEventFeature {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number];
  };
  properties: NewsEvent;
}

export interface NewsEventsResponse {
  type: 'FeatureCollection';
  features: NewsEventFeature[];
  meta: {
    total: number;
    bbox: [number, number, number, number];
    date_range: {
      start: string;
      end: string;
    };
  };
}

export interface NewsEventDetails extends NewsEvent {
  coordinates: [number, number];
  geocode_status?: string;
  extracted_at?: string;
  geocoded_at?: string;
  location_state?: string;
  location_country?: string;
  domain?: string;
  supporting_details?: string;
  confidence_score?: number;
  citations?: string;
  admin_areas?: string;
  timestamp?: string;
}

/**
 * Fetch news events by bbox with optional filters
 */
export async function fetchNewsEvents(params: {
  bbox: { minLng: number; minLat: number; maxLng: number; maxLat: number };
  search?: string;
  start_date?: string; // YYYY-MM-DD
  end_date?: string; // YYYY-MM-DD
  limit?: number;
}): Promise<NewsEventsResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set('bbox', `${params.bbox.minLng},${params.bbox.minLat},${params.bbox.maxLng},${params.bbox.maxLat}`);

  if (params.search) {
    searchParams.set('search', params.search);
  }
  if (params.start_date) {
    searchParams.set('start_date', params.start_date);
  }
  if (params.end_date) {
    searchParams.set('end_date', params.end_date);
  }
  if (params.limit) {
    searchParams.set('limit', params.limit.toString());
  }

  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/articles?${searchParams}`, { headers });

  if (!response.ok) {
    throw new Error(`Events fetch failed: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Fetch a single news event by article URL and location number
 */
export async function fetchNewsEventDetails(articleUrl: string, locationNumber: number): Promise<NewsEventDetails> {
  const headers = await getAuthHeaders();
  const encodedUrl = encodeURIComponent(articleUrl);
  const response = await fetch(`${API_BASE}/articles/${encodedUrl}/${locationNumber}`, { headers });

  if (!response.ok) {
    throw new Error(`Event details fetch failed: ${response.statusText}`);
  }
  return response.json();
}

export interface SocialPost {
  tweet_id: string;
  tweet_date: string;
  location_number: number;
  location_name: string;
  location_type: string;
  location_event: string;
  location_country: string;
  platform: string;
  tweet_country_code: string;
}

export interface SocialPostFeature {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number];
  };
  properties: SocialPost;
}

export interface SocialPostsResponse {
  type: 'FeatureCollection';
  features: SocialPostFeature[];
  meta: {
    total: number;
    bbox: [number, number, number, number];
    date_range: {
      start: string;
      end: string;
    };
  };
}

export interface SocialPostDetails extends SocialPost {
  // Location details
  coordinates: [number, number];
  place_id: string;
  formatted_address: string;
  l0_country: string;
  l1_province: string;
  l2_district: string;
  l3_locality: string;
  location_state: string;
  geocode_status: string;
  streetview_url: string;
  tweet_confidence: number;
  has_native_coords: boolean;
  extracted_at: string;
  geocoded_at: string;
  // Post metadata from soc_med_search_data
  text_original: string;
  text_translated: string;
  text_preview: string;
  source_language: string;
  created_at: string;
  collected_at: string;
  scored_at: string;
  author_username: string;
  author_location: string;
  author_followers: number;
  author_is_verified: boolean;
  likes: number;
  retweets: number;
  views: number;
  hashtags: string;
  mentions: string;
  has_media: boolean;
  extracted_media_urls: string;
  geographic_relevance: number;
  topical_relevance: number;
  source_authority: number;
  confidence_score: number;
  tier: string;
  geographic_entities: string;
  topic_tags: string;
  reasoning: string;
  enrichment_context: string;
}

export async function fetchSocialPosts(params: {
  bbox: { minLng: number; minLat: number; maxLng: number; maxLat: number };
  search?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
}): Promise<SocialPostsResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set('bbox', `${params.bbox.minLng},${params.bbox.minLat},${params.bbox.maxLng},${params.bbox.maxLat}`);

  if (params.search) {
    searchParams.set('search', params.search);
  }
  if (params.start_date) {
    searchParams.set('start_date', params.start_date);
  }
  if (params.end_date) {
    searchParams.set('end_date', params.end_date);
  }
  if (params.limit) {
    searchParams.set('limit', params.limit.toString());
  }

  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/social?${searchParams}`, { headers });

  if (!response.ok) {
    throw new Error(`Social posts fetch failed: ${response.statusText}`);
  }
  return response.json();
}

export async function fetchSocialPostDetails(tweetId: string, locationNumber: number): Promise<SocialPostDetails> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/social/${encodeURIComponent(tweetId)}/${locationNumber}`, { headers });

  if (!response.ok) {
    throw new Error(`Social post details fetch failed: ${response.statusText}`);
  }
  return response.json();
}

// ── Research Mode ───────────────────────────────────────────────────────────

export interface ResearchForm {
  form_id: number;
  form_title: string;
  approved_count: number;
  /** Last day this form has approved data, YYYY-MM-DD. Null if unknown. */
  last_submission: string | null;
}

export interface ResearchProject {
  project_id: number;
  project_name: string;
  organization_id: string;
  approved_count: number;
  /** Most recent `last_submission` across this project's forms. */
  last_submission: string | null;
  forms: ResearchForm[];
}

export interface ResearchProjectsResponse {
  projects: ResearchProject[];
}

export async function fetchResearchProjects(): Promise<ResearchProjectsResponse> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/agent/research/projects`, { headers });

  if (!response.ok) {
    throw new Error(`Research projects fetch failed: ${response.statusText}`);
  }
  return response.json();
}

export interface AgentQuota {
  allowed: boolean;
  /** null when the caller has no chargeable org (sys-admin-only users); the
   *  chat route bypasses the gate for these users, so the UI should also
   *  treat them as always-allowed. */
  chargeOrg: string | null;
  monthlyRemaining:   number;
  purchasedRemaining: number;
}

export async function fetchAgentQuota(): Promise<AgentQuota> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/agent/quota`, { headers });
  if (!response.ok) {
    throw new Error(`Quota fetch failed: ${response.statusText}`);
  }
  return response.json();
}

export interface AgentEstimate {
  inputTokens:      number;
  maxOutputPerTurn: number;
  turns:            number;
  totalEstimate:    number;
  /** Server-side ceiling on inputTokens. Absent on older servers. */
  inputLimit?:      number;
}

export async function fetchAgentEstimate(params: {
  message: string;
  mode?: 'map' | 'research';
}): Promise<AgentEstimate> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/agent/estimate`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!response.ok) {
    throw new Error(`Estimate fetch failed: ${response.statusText}`);
  }
  return response.json();
}

export interface CountryInfo {
  name: string;
  count: number;
}

// Export document as Word (.docx)
export async function exportDocument(
  markdown: string,
  type: 'collection' | 'report' | 'analyze' | 'network' | 'biosurveillance',
  meta: { title?: string; country?: string; date?: string }
): Promise<void> {
  const auth = await getFirebaseAuth();
  const user = auth.currentUser;
  const token = user ? await user.getIdToken() : '';

  const response = await fetch(`${API_BASE}/agent/export`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ markdown, type, meta }),
  });

  if (!response.ok) throw new Error('Export failed');

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = meta.title ? `${meta.title}.docx` : 'document.docx';
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * `startDate` is already resolved by the caller (YYYY-MM-DD) because the
 * research window is anchored on the selected forms' last day of data, not on
 * today — see timeWindowStartDate in ResearchWorkspace. Pass null for "all".
 */
export async function fetchCountries(projectId: number, startDate?: string | null, formIds?: number[]): Promise<{ countries: CountryInfo[] }> {
  const params = new URLSearchParams({ projectId: String(projectId) });
  if (startDate) params.set('startDate', startDate);
  if (formIds && formIds.length > 0) params.set('formIds', formIds.join(','));

  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/agent/research/countries?${params}`, { headers });

  if (!response.ok) {
    throw new Error(`Countries fetch failed: ${response.statusText}`);
  }
  return response.json();
}

// ── Session management ──────────────────────────────────────────────────────

export interface SessionMetadata {
  id: string;
  userId: string;
  mode: 'report' | 'collection' | 'analyze' | 'network' | 'biosurveillance' | 'survey' | 'prediction_market' | 'map_creator' | 'mosaics';
  title: string;
  appName: string;
  messageCount: number;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface SessionHistoryMessage {
  role: 'user' | 'assistant';
  text: string;
  timestamp: string | null;
  // Present only on resumed Prediction Market Agent turns — carries the
  // analyze_form tool response so the report re-renders on load.
  predictionMarketReport?: {
    report_data: unknown;
    download_filename?: string;
    matched_markets?: unknown;
  };
  // Present on resumed Map Creator turns: the map and its run notes.
  geojson?: import('../components/research/GeoJsonMap').MapGeoJson;
  mapCreator?: import('../hooks/useChat').ChatMessage['mapCreator'];
}

export interface SessionHistoryResponse {
  messages: SessionHistoryMessage[];
  sessionLost?: boolean;
  metadata?: { mode: string; title: string };
}

export async function fetchSessions(mode?: string): Promise<{ sessions: SessionMetadata[] }> {
  const headers = await getAuthHeaders();
  const params = mode ? `?mode=${encodeURIComponent(mode)}` : '';
  const response = await fetch(`${API_BASE}/agent/sessions${params}`, { headers });
  if (!response.ok) throw new Error(`Sessions fetch failed: ${response.statusText}`);
  return response.json();
}

export async function fetchSessionHistory(sessionId: string): Promise<SessionHistoryResponse> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/agent/sessions/${encodeURIComponent(sessionId)}/history`, { headers });
  if (!response.ok) throw new Error(`Session history fetch failed: ${response.statusText}`);
  return response.json();
}

export async function renameSession(sessionId: string, title: string): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/agent/sessions/${encodeURIComponent(sessionId)}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ title }),
  });
  if (!response.ok) throw new Error(`Session rename failed: ${response.statusText}`);
}

export async function deleteSession(sessionId: string): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/agent/sessions/${encodeURIComponent(sessionId)}`, {
    method: 'DELETE',
    headers,
  });
  if (!response.ok) throw new Error(`Session delete failed: ${response.statusText}`);
}

// Explicit save for map-mode (mosaics) chats that stay ephemeral until the
// user hits the save button. Idempotent: if the session was already saved,
// the server just updates the title.
export async function saveSession(params: {
  sessionId: string;
  title?: string;
  firstMessage?: string;
  messageCount?: number;
}): Promise<{ id: string; title: string }> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/agent/sessions/save`, {
    method: 'POST',
    headers,
    body: JSON.stringify(params),
  });
  if (!response.ok) throw new Error(`Session save failed: ${response.statusText}`);
  const data = await response.json();
  return { id: data.id, title: data.title };
}

// ============================================================================
// MONITOR
// ============================================================================

export interface MonitorFeature {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number]; // [lon, lat]
  };
  properties: {
    guid: string;
    name: string;
    category: string;
  };
}

export interface MonitorResponse {
  type: 'FeatureCollection';
  features: MonitorFeature[];
  meta: {
    total: number;
    categories: string[];
  };
}

export interface MonitorUploadResponse {
  inserted: number;
  duplicates: number;
  errors: string[];
}

export interface MonitorCategory {
  name: string;
  count: number;
}

/** GET /monitor/quota: rows the org holds against its `monitors` limit. */
export interface MonitorQuota {
  orgId: string;
  used: number;
  limit: number;
}

export async function fetchMonitorQuota(orgId?: string): Promise<MonitorQuota> {
  const headers = await getAuthHeaders();
  const url = orgId ? `${API_BASE}/monitor/quota?orgId=${encodeURIComponent(orgId)}` : `${API_BASE}/monitor/quota`;
  const response = await fetch(url, { headers });
  if (!response.ok) throw new Error(`Failed to fetch monitor quota: ${response.statusText}`);
  return response.json();
}

export async function fetchMonitorData(orgId?: string): Promise<MonitorResponse> {
  const headers = await getAuthHeaders();
  const url = orgId ? `${API_BASE}/monitor/data?orgId=${encodeURIComponent(orgId)}` : `${API_BASE}/monitor/data`;
  const response = await fetch(url, { headers });
  if (!response.ok) throw new Error(`Monitor data fetch failed: ${response.statusText}`);
  return response.json();
}

export async function fetchMonitorCategories(orgId?: string): Promise<{ categories: MonitorCategory[] }> {
  const headers = await getAuthHeaders();
  const url = orgId ? `${API_BASE}/monitor/categories?orgId=${encodeURIComponent(orgId)}` : `${API_BASE}/monitor/categories`;
  const response = await fetch(url, { headers });
  if (!response.ok) throw new Error(`Monitor categories fetch failed: ${response.statusText}`);
  return response.json();
}

export async function uploadMonitorCSV(file: File, orgId?: string): Promise<MonitorUploadResponse> {
  // Must NOT use getAuthHeaders() — it sets Content-Type to JSON which breaks FormData
  const auth = await getFirebaseAuth();
  const user = auth.currentUser;
  const token = user ? await user.getIdToken() : '';

  const formData = new FormData();
  formData.append('file', file);

  const url = orgId ? `${API_BASE}/monitor/upload?orgId=${encodeURIComponent(orgId)}` : `${API_BASE}/monitor/upload`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(err.error || `Upload failed: ${response.statusText}`);
  }
  return response.json();
}

// Monitor Status Types
export interface MonitorMajorEvent {
  title: string;
  source: string;
  severity: string;
  h3: string;
  event_timestamp: string | null;
}

export interface MonitorStatus {
  guid: string;
  name: string;
  category: string;
  latitude: number;
  longitude: number;
  assessment_date: string;
  ongoing_threat_level: string;
  daily_change_status: string;
  summary: string | null;
  days_at_current_level: number | null;
  major_events: MonitorMajorEvent[];
  h3_article_count: number | null;
  h3_social_count: number | null;
  weighted_sentiment: number | null;
}

export async function fetchMonitorStatus(orgId?: string): Promise<{ statuses: MonitorStatus[] }> {
  const headers = await getAuthHeaders();
  const url = orgId ? `${API_BASE}/monitor/status?orgId=${encodeURIComponent(orgId)}` : `${API_BASE}/monitor/status`;
  const response = await fetch(url, { headers });
  if (!response.ok) throw new Error(`Monitor status fetch failed: ${response.statusText}`);
  return response.json();
}

export async function fetchMonitorStatusHistory(guid: string, orgId?: string): Promise<{ statuses: MonitorStatus[] }> {
  const headers = await getAuthHeaders();
  const params = orgId ? `?orgId=${encodeURIComponent(orgId)}` : '';
  const response = await fetch(`${API_BASE}/monitor/status/history/${encodeURIComponent(guid)}${params}`, { headers });
  if (!response.ok) throw new Error(`Monitor status history fetch failed: ${response.statusText}`);
  return response.json();
}

// ============================================================
// Event Alerts (BigQuery culmen-alerting-platform.scrapers.events_dedupe)
// ============================================================

export interface EventSourceArticle {
  id: string | null;
  url: string | null;
  source_domain: string | null;
  title: string | null;
  source_language: string | null;
  content_original: string | null;
  content_translated: string | null;
  severity: number | null;
  urgency: number | null;
  novelty: number | null;
  confidence: number | null;
  discovered_at: string | null;
}

export interface EventAlert {
  canonical_id: string;
  country_code: string | null;
  country_name: string | null;
  topic: string | null;
  subtopic: string | null;
  title: string | null;
  summary_full: string | null;
  summary_one_line: string | null;
  location_name: string | null;
  location_type: string | null;
  location_country: string | null;
  location_country_code: string | null;
  location_lat: number | null;
  location_lon: number | null;
  location_precision: string | null;
  max_severity: number | null;
  max_urgency: number | null;
  max_novelty: number | null;
  max_topic_confidence: number | null;
  is_major_event: boolean | null;
  source_count: number | null;
  article_count: number | null;
  event_date: string | null;
  first_reported_at: string | null;
  /** Advances whenever an article is added; see Custom UI review. */
  last_updated_at?: string | null;
  source_articles: EventSourceArticle[] | string[];  // strings from list endpoint, objects from detail
}

export interface EventAlertsResponse {
  events: EventAlert[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

// /my-events no longer returns total — it's deferred to a second request
// via /my-events/count so the page renders without waiting for COUNT(*).
export interface MyEventAlertsPage {
  events: EventAlert[];
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface MyEventAlertsCount {
  total: number;
}

export interface EventAlertFilters {
  countries: string[];
  topics: string[];
  subtopics: string[];
  subtopicsByTopic: Record<string, string[]>;
}

export interface AlertMapPoint {
  canonical_id: string;
  topic: string;
  summary_one_line: string | null;
  location_name: string | null;
  location_country: string | null;
  location_lat: number;
  location_lon: number;
  max_severity: number;
  max_urgency: number;
  first_reported_at: string | null;
  source_domains: string[];
}

export interface GroundingEvent {
  id?: string | null;
  title?: string | null;
  summary: string | null;
  topic: string;
  country: string | null;
  severity: number;
  urgency: number;
  reported: string | null;
  location?: string | null;
  lat?: number | null;
  lon?: number | null;
  /** Up to three source articles the org may see (url, title, domain). */
  articles?: Array<{ url: string; title: string | null; domain: string | null }>;
}

export async function fetchGroundingEvents(params: {
  countries: string[];
  topics: string[];
  timeWindow: string;
}): Promise<{ events: GroundingEvent[] }> {
  const sp = new URLSearchParams();
  if (params.countries.length > 0) sp.set('countries', params.countries.join(','));
  if (params.topics.length > 0) sp.set('topics', params.topics.join(','));
  sp.set('timeWindow', params.timeWindow);

  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/alerts/events/grounding?${sp.toString()}`, { headers });
  if (!response.ok) throw new Error('Failed to fetch grounding events');
  return response.json();
}

export async function fetchAlertMapData(params?: {
  bbox?: string; // "minLon,minLat,maxLon,maxLat"
  from?: string; // YYYY-MM-DD, inclusive. Both from + to required together.
  to?: string;
  topic?: string;
  severity?: number;
  urgency?: number;
  search?: string;
}): Promise<{ points: AlertMapPoint[] }> {
  const sp = new URLSearchParams();
  if (params?.bbox) sp.set('bbox', params.bbox);
  if (params?.from && params?.to) {
    sp.set('from', params.from);
    sp.set('to', params.to);
  }
  if (params?.topic) sp.set('topic', params.topic);
  if (params?.severity) sp.set('severity', String(params.severity));
  if (params?.urgency) sp.set('urgency', String(params.urgency));
  if (params?.search) sp.set('search', params.search);

  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/alerts/events/map?${sp.toString()}`, { headers });
  if (!response.ok) throw new Error(`Alert map data fetch failed: ${response.statusText}`);
  return response.json();
}

export async function voteOnEvent(canonicalId: string, vote: 'like' | 'dislike'): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/alerts/events/vote`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ canonical_id: canonicalId, vote }),
  });
  if (!response.ok) throw new Error('Failed to record vote');
}

/** `content: false` skips the article-body lookup (the slow query) and
 *  returns the row with article metadata only. */
export async function fetchEventDetail(canonicalId: string, opts: { content?: boolean } = {}): Promise<{ event: EventAlert }> {
  const headers = await getAuthHeaders();
  const qs = opts.content === false ? '?content=0' : '';
  const response = await fetch(`${API_BASE}/alerts/events/${encodeURIComponent(canonicalId)}${qs}`, { headers });
  if (!response.ok) throw new Error(`Event detail fetch failed: ${response.statusText}`);
  return response.json();
}

export async function fetchEventAlerts(params: {
  country?: string;
  countries?: string[];  // multi-value (OR)
  from?: string; // YYYY-MM-DD, inclusive; matched against COALESCE(event_date, first_reported_at)
  to?: string;   // YYYY-MM-DD, inclusive; must be >= from. Both required together.
  topic?: string;
  topics?: string[];     // multi-value (OR)
  subtopic?: string;
  severity?: number; // min 1-5
  urgency?: number;  // min 1-5
  source?: string;   // 'telegram' | 'twitter' | 'web'
  search?: string;
  /** Exactly these canonical ids (max 200); paging and the focus do not apply. */
  ids?: string[];
  limit?: number;
  offset?: number;
}): Promise<EventAlertsResponse> {
  const sp = new URLSearchParams();
  if (params.ids && params.ids.length > 0) sp.set('ids', params.ids.join(','));
  if (params.countries && params.countries.length > 0) {
    sp.set('countries', params.countries.join(','));
  } else if (params.country) {
    sp.set('country', params.country);
  }
  if (params.from && params.to) {
    sp.set('from', params.from);
    sp.set('to', params.to);
  }
  if (params.topics && params.topics.length > 0) {
    sp.set('topics', params.topics.join(','));
  } else if (params.topic) {
    sp.set('topic', params.topic);
  }
  if (params.subtopic) sp.set('subtopic', params.subtopic);
  if (params.search) sp.set('search', params.search);
  if (params.severity) sp.set('severity', String(params.severity));
  if (params.urgency) sp.set('urgency', String(params.urgency));
  if (params.source) sp.set('source', params.source);
  sp.set('limit', String(params.limit ?? 50));
  sp.set('offset', String(params.offset ?? 0));

  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/alerts/events?${sp.toString()}`, { headers });
  if (!response.ok) throw new Error(`Event alerts fetch failed: ${response.statusText}`);
  return response.json();
}

export type EventAlertGroupBy = 'day' | 'week' | 'month' | 'country' | 'topic' | 'subtopic' | 'severity';
export interface EventAlertBucket { key: string | null; count: number }

/** GET /alerts/events?groupBy=… — the same filters as fetchEventAlerts,
 *  one grouped count instead of rows. Used by the Custom UI chart blocks
 *  so a chart and the list beside it always agree on which rows exist. */
export async function fetchEventAlertBuckets(params: {
  countries?: string[];
  from?: string;
  to?: string;
  topics?: string[];
  subtopic?: string;
  severity?: number;
  urgency?: number;
  source?: string;
  search?: string;
  groupBy: EventAlertGroupBy;
}): Promise<{ groupBy: EventAlertGroupBy; buckets: EventAlertBucket[] }> {
  const sp = new URLSearchParams();
  if (params.countries && params.countries.length > 0) sp.set('countries', params.countries.join(','));
  if (params.from && params.to) { sp.set('from', params.from); sp.set('to', params.to); }
  if (params.topics && params.topics.length > 0) sp.set('topics', params.topics.join(','));
  if (params.subtopic) sp.set('subtopic', params.subtopic);
  if (params.severity) sp.set('severity', String(params.severity));
  if (params.urgency) sp.set('urgency', String(params.urgency));
  if (params.source) sp.set('source', params.source);
  if (params.search) sp.set('search', params.search);
  sp.set('groupBy', params.groupBy);
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/alerts/events?${sp.toString()}`, { headers });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Event buckets fetch failed: ${response.statusText}`);
  }
  return response.json();
}

export async function fetchMyEventAlerts(params: {
  limit?: number;
  offset?: number;
}): Promise<MyEventAlertsPage> {
  const sp = new URLSearchParams();
  sp.set('limit', String(params.limit ?? 50));
  sp.set('offset', String(params.offset ?? 0));

  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/alerts/my-events?${sp.toString()}`, { headers });
  if (!response.ok) throw new Error(`My events fetch failed: ${response.statusText}`);
  return response.json();
}

// Total count for the same WHERE — fired separately from the page query so
// the page can render without blocking on COUNT(*).
export async function fetchMyEventAlertsCount(): Promise<MyEventAlertsCount> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/alerts/my-events/count`, { headers });
  if (!response.ok) throw new Error(`My events count fetch failed: ${response.statusText}`);
  return response.json();
}

export async function fetchEventAlertFilters(): Promise<EventAlertFilters> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/alerts/events/filters`, { headers });
  if (!response.ok) throw new Error(`Event alert filters fetch failed: ${response.statusText}`);
  return response.json();
}

// ============================================================
// Daily Summaries (BigQuery culmen-alerting-platform.scrapers.daily_summaries)
// ============================================================

export interface ActiveEvent {
  title: string;
  location_name: string;
  severity: number;
  summary: string;
}

export interface KeyActor {
  name: string;
  role: string;
  country: string;
}

export interface GeographicHotspot {
  location_name: string;
  country: string;
  event_count: number;
  significance: string;
}

export interface DailySummary {
  summary_id: string;
  country_code: string;
  country_name: string;
  topic: string;
  summary_date: string;
  generated_at: string;
  situation_overview: string | null;
  active_events: ActiveEvent[];
  key_actors: KeyActor[];
  geographic_hotspots: GeographicHotspot[];
  trend_direction: string | null;
  article_count: number | null;
  source_domains: string[];
  model_version: string | null;
  prompt_version: string | null;
  previous_summary_id: string | null;
}

export interface DailySummariesResponse {
  summaries: DailySummary[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface DailySummaryFilters {
  countries: string[];
  topics: string[];
}

export async function fetchDailySummaries(params: {
  country?: string;
  date?: string;
  topic?: string;
  limit?: number;
  offset?: number;
}): Promise<DailySummariesResponse> {
  const sp = new URLSearchParams();
  if (params.country) sp.set('country', params.country);
  if (params.date) sp.set('date', params.date);
  if (params.topic) sp.set('topic', params.topic);
  sp.set('limit', String(params.limit ?? 50));
  sp.set('offset', String(params.offset ?? 0));

  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/alerts/daily-summaries?${sp.toString()}`, { headers });
  if (!response.ok) throw new Error(`Daily summaries fetch failed: ${response.statusText}`);
  return response.json();
}

export async function fetchDailySummaryFilters(): Promise<DailySummaryFilters> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/alerts/daily-summaries/filters`, { headers });
  if (!response.ok) throw new Error(`Daily summary filters fetch failed: ${response.statusText}`);
  return response.json();
}

// ============================================================
// Hourly Summaries (BigQuery culmen-alerting-platform.scrapers.hourly_summaries)
// ============================================================

export interface HourlyAlert {
  alert_id: string | null;
  severity: string | null;
  severity_rationale: string | null;
  event_title: string | null;
  event_summary: string | null;
  event_location: string | null;
  key_actors: string[];
  is_novel: boolean | null;
  is_escalation: boolean | null;
  escalation_from: string | null;
  prior_hour_present: boolean | null;
  source_count: number | null;
  source_domains: string[];
  first_reported_at: string | null;
  prior_alert_id: string | null;
}

export interface HourlySummary {
  run_id: string;
  country_code: string;
  country_name: string;
  topic: string;
  alert_hour: string;
  situation_delta: string | null;
  trend_direction: string | null;
  articles_reviewed: number | null;
  daily_summary_id: string | null;
  alerts: HourlyAlert[];
  model_version: string | null;
  prompt_version: string | null;
  generated_at: string;
}

export interface HourlySummariesResponse {
  summaries: HourlySummary[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface HourlySummaryFilters {
  countries: string[];
  topics: string[];
}

export async function fetchHourlySummaries(params: {
  country?: string;
  date?: string;
  hour?: number;
  topic?: string;
  limit?: number;
  offset?: number;
}): Promise<HourlySummariesResponse> {
  const sp = new URLSearchParams();
  if (params.country) sp.set('country', params.country);
  if (params.date) sp.set('date', params.date);
  if (params.hour != null) sp.set('hour', String(params.hour));
  if (params.topic) sp.set('topic', params.topic);
  sp.set('limit', String(params.limit ?? 50));
  sp.set('offset', String(params.offset ?? 0));

  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/alerts/hourly-summaries?${sp.toString()}`, { headers });
  if (!response.ok) throw new Error(`Hourly summaries fetch failed: ${response.statusText}`);
  return response.json();
}

export async function fetchHourlySummaryFilters(): Promise<HourlySummaryFilters> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/alerts/hourly-summaries/filters`, { headers });
  if (!response.ok) throw new Error(`Hourly summary filters fetch failed: ${response.statusText}`);
  return response.json();
}

// ============================================================
// API Key Management (admin endpoints)
// ============================================================

export interface ApiKey {
  id: string;
  keyPrefix: string;
  name: string;
  orgId: string;
  createdBy: string;
  createdAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  lastUsedAt: string | null;
  scopes: string[];
  rateLimitPerMinute: number;
}

export interface CreateApiKeyResponse {
  key: string;
  id: string;
  name: string;
  orgId: string;
  expiresAt: string;
}

export async function createApiKey(params: {
  name: string;
  orgId: string;
  expiresInDays?: number;
}): Promise<CreateApiKeyResponse> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/admin/api-keys`, {
    method: 'POST',
    headers,
    body: JSON.stringify(params),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to create API key');
  }
  return response.json();
}

export async function listApiKeys(): Promise<{ keys: ApiKey[] }> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/admin/api-keys`, { headers });
  if (!response.ok) throw new Error('Failed to list API keys');
  return response.json();
}

export async function revokeApiKey(id: string): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/admin/api-keys/${id}`, {
    method: 'DELETE',
    headers,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to revoke API key');
  }
}

// ============================================================
// Alert Subscriptions
// ============================================================

export interface AlertSubscription {
  id: string;
  userId: string;
  name: string;
  countries: string[];
  topics: string[];
  // Optional free-form keywords. When non-empty, narrows matches further:
  // an event must contain at least one keyword (substring, case-insensitive)
  // in summary_one_line or summary_full to fire.
  keywords?: string[];
  smsEnabled: boolean;
  emailHourly: boolean;
  emailDaily: boolean;
  pushNearRealTime: boolean;
  pushSevThreshold: number;
  pushUrgThreshold: number;
  pushHourly: boolean;
  pushDaily: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

export async function listAlertSubscriptions(): Promise<{ subscriptions: AlertSubscription[] }> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/alerts/subscriptions`, { headers });
  if (!response.ok) throw new Error('Failed to list subscriptions');
  return response.json();
}

export async function createAlertSubscription(data: Omit<AlertSubscription, 'id' | 'userId' | 'createdAt' | 'updatedAt'>): Promise<{ id: string }> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/alerts/subscriptions`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to create subscription');
  }
  return response.json();
}

export async function updateAlertSubscription(id: string, data: Omit<AlertSubscription, 'id' | 'userId' | 'createdAt' | 'updatedAt'>): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/alerts/subscriptions/${id}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to update subscription');
  }
}

export async function deleteAlertSubscription(id: string): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/alerts/subscriptions/${id}`, {
    method: 'DELETE',
    headers,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to delete subscription');
  }
}

// ============================================================
// Organization Alert Profiles
// ============================================================

export interface OrgAlertProfile {
  id: string;
  orgId: string;
  createdBy: string;
  name: string;
  countries: string[];
  topics: string[];
  smsEnabled: boolean;
  emailHourly: boolean;
  emailDaily: boolean;
  pushNearRealTime: boolean;
  pushSevThreshold: number;
  pushUrgThreshold: number;
  pushHourly: boolean;
  pushDaily: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

export async function listOrgAlertProfiles(orgId: string): Promise<{ profiles: OrgAlertProfile[] }> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/alerts/profiles?orgId=${encodeURIComponent(orgId)}`, { headers });
  if (!response.ok) throw new Error('Failed to list profiles');
  return response.json();
}

export async function createOrgAlertProfile(data: { orgId: string } & Omit<OrgAlertProfile, 'id' | 'orgId' | 'createdBy' | 'createdAt' | 'updatedAt'> & { orgId: string }): Promise<{ id: string }> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/alerts/profiles`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to create profile');
  }
  return response.json();
}

export async function updateOrgAlertProfile(id: string, data: Omit<OrgAlertProfile, 'id' | 'orgId' | 'createdBy' | 'createdAt' | 'updatedAt'>): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/alerts/profiles/${id}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to update profile');
  }
}

export async function deleteOrgAlertProfile(id: string): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/alerts/profiles/${id}`, {
    method: 'DELETE',
    headers,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to delete profile');
  }
}

// ============================================================
// User Phone Number
// ============================================================

export async function saveUserPhone(phoneCountryCode: string, phoneNumber: string): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/user/phone`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ phoneCountryCode, phoneNumber }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to save phone number');
  }
}

export async function deleteUserPhone(): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/user/phone`, {
    method: 'DELETE',
    headers,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to remove phone number');
  }
}

// ============================================================
// Taxonomy admin (sys_admin:admin only)
// ============================================================

export interface TaxonomyTerm {
  en_label: string;
  native_term: string;
}

export interface TaxonomyCountry {
  name: string;
  fips: string;
  lang: string;
}

export interface TaxonomyExclusion {
  country: string;
  category: string;
  term: string;
}

export interface TaxonomyDoc {
  version: number;
  terms: Record<string, Record<string, TaxonomyTerm[]>>;
  countries: Record<string, TaxonomyCountry>;
  exclusions: TaxonomyExclusion[];
  anchors: Record<string, Record<string, string[]>>;
  en_date_langs: string[];
  anchor_langs: string[];
  publishedAt?: string | null;
  publishedBy?: string | null;
  draftedAt?: string | null;
  draftedBy?: string | null;
  baseVersion?: number;
}

export interface TaxonomyStatsRow {
  country: string;
  category: string;
  term: string;
  lang: string;
  hits: number;
  last_seen: string | null;
  excluded: boolean;
}

export interface TaxonomyStatsResponse {
  rows: TaxonomyStatsRow[];
  total: number;
  universe: number;
  limit: number;
  offset: number;
  window: string;
}

export interface ValidationIssue { path: string; message: string }

export async function fetchPublishedTaxonomy(): Promise<TaxonomyDoc> {
  const headers = await getAuthHeaders();
  const r = await fetch(`${API_BASE}/admin/taxonomy/published`, { headers });
  if (!r.ok) throw new Error('Failed to load published taxonomy');
  return r.json();
}

export async function fetchDraftTaxonomy(): Promise<TaxonomyDoc | null> {
  const headers = await getAuthHeaders();
  const r = await fetch(`${API_BASE}/admin/taxonomy/draft`, { headers });
  if (!r.ok) throw new Error('Failed to load draft taxonomy');
  return r.json();
}

export async function saveDraftTaxonomy(payload: Omit<TaxonomyDoc, 'publishedAt' | 'publishedBy' | 'draftedAt' | 'draftedBy' | 'version' | 'baseVersion'>): Promise<{ baseVersion: number }> {
  const headers = await getAuthHeaders();
  const r = await fetch(`${API_BASE}/admin/taxonomy/draft`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    const issues = (err.issues as ValidationIssue[] | undefined) ?? [];
    const msg = issues.length
      ? `${err.error || 'Invalid taxonomy'}: ${issues.map((i) => `${i.path} ${i.message}`).join('; ')}`
      : (err.error || 'Failed to save draft');
    throw new Error(msg);
  }
  return r.json();
}

export async function discardTaxonomyDraft(): Promise<void> {
  const headers = await getAuthHeaders();
  const r = await fetch(`${API_BASE}/admin/taxonomy/discard-draft`, {
    method: 'POST',
    headers,
  });
  if (!r.ok) throw new Error('Failed to discard draft');
}

export async function publishTaxonomy(): Promise<void> {
  const headers = await getAuthHeaders();
  const r = await fetch(`${API_BASE}/admin/taxonomy/publish`, {
    method: 'POST',
    headers,
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to publish');
  }
}

export interface TaxonomyHistoryEntry {
  version: number;
  publishedAt: string | null;
  publishedBy: string | null;
}

export async function fetchTaxonomyHistory(): Promise<{ versions: TaxonomyHistoryEntry[] }> {
  const headers = await getAuthHeaders();
  const r = await fetch(`${API_BASE}/admin/taxonomy/history`, { headers });
  if (!r.ok) throw new Error('Failed to load history');
  return r.json();
}

export async function rollbackTaxonomy(version: number): Promise<void> {
  const headers = await getAuthHeaders();
  const r = await fetch(`${API_BASE}/admin/taxonomy/rollback/${version}`, {
    method: 'POST',
    headers,
  });
  if (!r.ok) throw new Error('Failed to rollback');
}

export async function fetchTaxonomyStats(params: {
  window?: '7d' | '30d' | '90d';
  country?: string;
  category?: string;
  lang?: string;
  only_zero?: boolean;
  only_excluded?: boolean;
  sort?: 'hits' | 'country' | 'category' | 'term';
  dir?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}): Promise<TaxonomyStatsResponse> {
  const sp = new URLSearchParams();
  if (params.window) sp.set('window', params.window);
  if (params.country) sp.set('country', params.country);
  if (params.category) sp.set('category', params.category);
  if (params.lang) sp.set('lang', params.lang);
  if (params.only_zero) sp.set('only_zero', 'true');
  if (params.only_excluded) sp.set('only_excluded', 'true');
  if (params.sort) sp.set('sort', params.sort);
  if (params.dir) sp.set('dir', params.dir);
  if (params.limit != null) sp.set('limit', String(params.limit));
  if (params.offset != null) sp.set('offset', String(params.offset));

  const headers = await getAuthHeaders();
  const r = await fetch(`${API_BASE}/admin/taxonomy/stats?${sp}`, { headers });
  if (!r.ok) throw new Error('Failed to load stats');
  return r.json();
}

// ─────────────────────────────────────────────────────────────────────────
// Support / Zendesk
// ─────────────────────────────────────────────────────────────────────────

export interface SupportTicketSummary {
  id: number;
  subject: string;
  status: string;       // 'new' | 'open' | 'pending' | 'hold' | 'solved' | 'closed'
  priority: string | null;
  created_at: string;
  updated_at: string;
  url: string;          // user-facing Zendesk URL
}

export interface SupportTicketComment {
  id: number;
  author_id: number;
  author_name: string | null;
  body: string;
  created_at: string;
  public: boolean;
}

export interface SupportTicketDetail extends SupportTicketSummary {
  description: string;
  tags: string[];
  comments: SupportTicketComment[];
}

export async function fetchSupportTickets(includeClosed = false): Promise<{ tickets: SupportTicketSummary[]; count: number }> {
  const headers = await getAuthHeaders();
  const qs = includeClosed ? '?include_closed=true' : '';
  const r = await fetch(`${API_BASE}/support/tickets${qs}`, { headers });
  if (!r.ok) {
    const detail = await r.text().catch(() => '');
    throw new Error(`Failed to load support tickets: ${detail || r.statusText}`);
  }
  return r.json();
}

export async function fetchSupportTicket(id: number): Promise<{ ticket: SupportTicketDetail }> {
  const headers = await getAuthHeaders();
  const r = await fetch(`${API_BASE}/support/tickets/${id}`, { headers });
  if (!r.ok) {
    const detail = await r.text().catch(() => '');
    throw new Error(`Failed to load ticket: ${detail || r.statusText}`);
  }
  return r.json();
}

export async function createSupportTicket(params: {
  subject: string;
  description: string;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  /** Which page/surface the ticket was filed from. Server validates against
   *  an allowlist and stamps it onto the Zendesk ticket as `page:<source>`. */
  source?: 'map' | 'alerts' | 'research' | 'support';
}): Promise<{ ticket: SupportTicketSummary }> {
  const headers = await getAuthHeaders();
  const r = await fetch(`${API_BASE}/support/tickets`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!r.ok) {
    const detail = await r.text().catch(() => '');
    throw new Error(`Failed to create ticket: ${detail || r.statusText}`);
  }
  return r.json();
}

export async function replySupportTicket(id: number, body: string): Promise<void> {
  const headers = await getAuthHeaders();
  const r = await fetch(`${API_BASE}/support/tickets/${id}/reply`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ body }),
  });
  if (!r.ok) {
    const detail = await r.text().catch(() => '');
    throw new Error(`Failed to send reply: ${detail || r.statusText}`);
  }
}

// ── Public (free tier) API ──────────────────────────────────────────────
// Anonymous, no Firebase token. Backed by /api/public/* endpoints that
// enforce a Venezuela country-lock server-side. Only the four features
// exposed on the free tier (polygons, POIs, events, basemaps) have public
// variants — every other API call requires auth.

const PUBLIC_BASE = '/api/public';

export async function fetchPublicPOIsByBbox(params: {
  bbox: [number, number, number, number];
  categories: string[];
  limit?: number;
}): Promise<POIResponse> {
  const sp = new URLSearchParams({
    bbox: params.bbox.join(','),
    categories: params.categories.join(','),
  });
  if (params.limit) sp.set('limit', params.limit.toString());
  const r = await fetch(`${PUBLIC_BASE}/poi?${sp}`);
  if (!r.ok) throw new Error(`Public POI fetch failed: ${r.statusText}`);
  return r.json();
}

export async function fetchPublicAlertMapData(params?: {
  bbox?: string;
  from?: string; // YYYY-MM-DD, inclusive. Both from + to required together.
  to?: string;
  topic?: string;
  severity?: number;
  urgency?: number;
  search?: string;
}): Promise<{ points: AlertMapPoint[] }> {
  const sp = new URLSearchParams();
  if (params?.bbox) sp.set('bbox', params.bbox);
  if (params?.from && params?.to) {
    sp.set('from', params.from);
    sp.set('to', params.to);
  }
  if (params?.topic) sp.set('topic', params.topic);
  if (params?.severity) sp.set('severity', String(params.severity));
  if (params?.urgency) sp.set('urgency', String(params.urgency));
  if (params?.search) sp.set('search', params.search);
  const r = await fetch(`${PUBLIC_BASE}/events/map?${sp}`);
  if (!r.ok) throw new Error(`Public alert map fetch failed: ${r.statusText}`);
  return r.json();
}

export async function fetchPublicEventDetail(canonicalId: string): Promise<{ event: EventAlert }> {
  const r = await fetch(`${PUBLIC_BASE}/events/${encodeURIComponent(canonicalId)}`);
  if (!r.ok) throw new Error(`Public event detail fetch failed: ${r.statusText}`);
  return r.json();
}

export async function fetchPublicFeature(unitId: string): Promise<GeoJSONFeature> {
  const r = await fetch(`${PUBLIC_BASE}/tiles/feature/${encodeURIComponent(unitId)}`);
  if (!r.ok) throw new Error(`Public feature fetch failed: ${r.statusText}`);
  return r.json();
}

export async function fetchPublicFeatureByName(unitType: 'L1' | 'L2', name: string): Promise<GeoJSONFeature> {
  const r = await fetch(`${PUBLIC_BASE}/tiles/feature-by-name/${unitType}/${encodeURIComponent(name)}`);
  if (!r.ok) throw new Error(`Public feature-by-name fetch failed: ${r.statusText}`);
  return r.json();
}

export async function fetchPublicRiskSummaries(unitId: string): Promise<RiskSummariesResponse> {
  const r = await fetch(`${PUBLIC_BASE}/risk/summaries/${encodeURIComponent(unitId)}`);
  if (!r.ok) throw new Error(`Public risk summaries fetch failed: ${r.statusText}`);
  return r.json();
}

export async function fetchPublicAvailableQuarters(unitId: string): Promise<RiskQuartersResponse> {
  const r = await fetch(`${PUBLIC_BASE}/risk/quarters/${encodeURIComponent(unitId)}`);
  if (!r.ok) throw new Error(`Public quarters fetch failed: ${r.statusText}`);
  return r.json();
}

export async function fetchPublicPremisePhotos(params?: {
  bbox?: [number, number, number, number];
  limit?: number;
}): Promise<MyLocationsResponse> {
  const sp = new URLSearchParams();
  if (params?.bbox) sp.set('bbox', params.bbox.join(','));
  if (params?.limit) sp.set('limit', String(params.limit));
  const r = await fetch(`${PUBLIC_BASE}/premise-photos${sp.toString() ? `?${sp}` : ''}`);
  if (!r.ok) throw new Error(`Public premise photos fetch failed: ${r.statusText}`);
  return r.json();
}

export async function fetchPublicPOIPhotos(placeId: string): Promise<{ photos: { url: string; date: string | null }[] }> {
  const r = await fetch(`${PUBLIC_BASE}/poi/photos/${encodeURIComponent(placeId)}`);
  if (!r.ok) throw new Error(`Public POI photos fetch failed: ${r.statusText}`);
  return r.json();
}